import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Camera, ChevronRight, Pizza, Server, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, LevelBadge } from "../../components/primitives";
import { formatClock, formatCount, formatStamp } from "../../format";
import type { MonitoringEvent } from "../../types";
import { shortId } from "../requests/utils";
import { CopyButton, SourceCode } from "./bits";

/**
 * One event of the log: time, level, source / code, message, subject, folded repeats and what it is about
 * (pizza, branch, camera, instance). Expands to the details JSON and the traceback.
 */
export function EventRow({ event, branchName }: { event: MonitoringEvent; branchName: string | null }) {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const occurrences = event.repeats + 1;

  const toggleFromText = () => {
    // selecting text to copy it must not toggle the row
    if (window.getSelection()?.toString()) return;
    setOpen(!open);
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <li
        className={cn(
          "border-l-2 border-l-transparent px-1 py-2 sm:px-2",
          open && "bg-muted/20",
          event.level === "error" && "border-l-red-500/60",
        )}
      >
        <div className="flex items-start gap-1.5 sm:gap-2">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="mt-0.5 h-6 w-6 shrink-0 p-0"
              aria-label={open ? t("monitoring.events.log.collapse") : t("monitoring.events.log.expand")}
            >
              <ChevronRight className={cn("h-4 w-4 transition-transform", open && "rotate-90")} aria-hidden />
            </Button>
          </CollapsibleTrigger>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <time
                dateTime={new Date(event.ts).toISOString()}
                title={formatStamp(event.ts)}
                className="font-mono text-[11px] tabular-nums text-foreground/90"
              >
                {formatClock(event.ts)}
              </time>
              <Ago ts={event.ts} className="text-[11px] text-muted-foreground" />
              <LevelBadge level={event.level} />
              <SourceCode source={event.source} code={event.code} />
              {event.repeats > 0 && (
                <span
                  className="whitespace-nowrap rounded border border-border px-1 font-mono text-[10px] tabular-nums text-foreground/80"
                  title={t("monitoring.events.foldedHint")}
                >
                  ×{formatCount(occurrences)}
                  {event.last_ts && (
                    <span className="ml-1 text-muted-foreground">
                      {t("monitoring.events.log.until", { time: formatClock(event.last_ts) })}
                    </span>
                  )}
                </span>
              )}
            </div>
            <p
              onClick={toggleFromText}
              className={cn(
                "mt-1 cursor-pointer whitespace-pre-wrap break-words text-sm leading-snug",
                !open && "line-clamp-3",
              )}
            >
              {event.message}
            </p>
            {event.subject && (
              <div className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">{event.subject}</div>
            )}
            <EventMeta event={event} branchName={branchName} />
          </div>
        </div>
        <CollapsibleContent>
          <EventDetails event={event} branchName={branchName} />
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
}

function MetaItem({ icon: Icon, children, title }: { icon: typeof Pizza; children: ReactNode; title?: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1" title={title}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {children}
    </span>
  );
}

function EventMeta({ event, branchName }: { event: MonitoringEvent; branchName: string | null }) {
  const { t } = useLocalization();
  if (!event.pizza_id && !event.branch_id && !event.device_id && !event.instance) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
      {event.pizza_id && (
        <MetaItem icon={Pizza}>
          <Link
            to={`/pizzas/${encodeURIComponent(event.pizza_id)}`}
            className="break-all font-mono underline-offset-2 hover:text-foreground hover:underline"
            aria-label={t("monitoring.events.log.openPizza", { id: event.pizza_id })}
          >
            {event.pizza_id}
          </Link>
        </MetaItem>
      )}
      {event.branch_id && (
        <MetaItem icon={Store} title={event.branch_id}>
          <span className="break-words">{branchName ?? t("monitoring.unknownBranch")}</span>
        </MetaItem>
      )}
      {event.device_id && (
        <MetaItem icon={Camera}>
          <Link
            to={`/cameras/${encodeURIComponent(event.device_id)}`}
            title={event.device_id}
            className="font-mono underline-offset-2 hover:text-foreground hover:underline"
            aria-label={t("monitoring.events.log.openCamera", { id: event.device_id })}
          >
            {shortId(event.device_id)}
          </Link>
        </MetaItem>
      )}
      {event.instance && (
        <MetaItem icon={Server} title={t("monitoring.events.log.instanceHint", { role: event.role ?? "–" })}>
          <span className="break-all font-mono text-[10px]">{event.instance}</span>
        </MetaItem>
      )}
    </div>
  );
}

function EventDetails({ event, branchName }: { event: MonitoringEvent; branchName: string | null }) {
  const { t, intlLocale } = useLocalization();
  const stamp = (ts: number | null) =>
    ts ? new Date(ts).toLocaleString(intlLocale, { dateStyle: "medium", timeStyle: "medium" }) : "–";
  const details = event.details && Object.keys(event.details).length ? JSON.stringify(event.details, null, 2) : null;

  const fields: { label: string; value: ReactNode }[] = [
    { label: t("monitoring.events.detail.time"), value: stamp(event.ts) },
    ...(event.repeats > 0
      ? [
          { label: t("monitoring.events.detail.lastRepeat"), value: stamp(event.last_ts) },
          { label: t("monitoring.events.detail.occurrences"), value: formatCount(event.repeats + 1) },
        ]
      : []),
    { label: t("monitoring.events.detail.fingerprint"), value: event.fingerprint },
    ...(event.pizza_id ? [{ label: t("monitoring.col.pizza"), value: event.pizza_id }] : []),
    ...(event.branch_id
      ? [{ label: t("monitoring.col.branch"), value: `${branchName ?? t("monitoring.unknownBranch")} (${event.branch_id})` }]
      : []),
    ...(event.device_id ? [{ label: t("monitoring.events.detail.device"), value: event.device_id }] : []),
    { label: t("monitoring.events.detail.instance"), value: `${event.instance ?? "–"} · ${event.role ?? "–"}` },
    { label: t("monitoring.events.detail.id"), value: event.id },
  ];

  return (
    <div className="mt-2 flex flex-col gap-3 pb-1 pl-7 pr-1 sm:pl-8">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-[11px] sm:grid-cols-2">
        {fields.map((field) => (
          <div key={field.label} className="flex min-w-0 gap-2 border-b border-border/40 py-1">
            <dt className="w-28 shrink-0 text-muted-foreground">{field.label}</dt>
            <dd className="min-w-0 break-all font-mono">{field.value}</dd>
          </div>
        ))}
      </dl>
      {details && (
        <CodeBlock title={t("monitoring.events.detail.details")} text={details} what={t("monitoring.events.detail.details")} />
      )}
      {event.traceback && (
        <CodeBlock title={t("monitoring.events.detail.traceback")} text={event.traceback} what={t("monitoring.events.detail.traceback")} />
      )}
      {!details && !event.traceback && (
        <p className="text-[11px] text-muted-foreground">{t("monitoring.events.detail.nothingMore")}</p>
      )}
    </div>
  );
}

function CodeBlock({ title, text, what }: { title: string; text: string; what: string }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-muted-foreground">{title}</span>
        <CopyButton text={text} what={what} />
      </div>
      <pre
        tabIndex={0}
        aria-label={title}
        className="max-h-72 min-w-0 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/30 p-2 font-mono text-[11px] leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {text}
      </pre>
    </div>
  );
}
